import React from 'react';
import { AttachFile } from '@mui/icons-material';
import { Box, Chip, Link as MUILink, Stack, Typography } from '@mui/material';
import type { OutboundEmailAttachment } from '@/types/emailAdmin';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';

const formatAttachmentSize = (fileSize: number | null): string | null => {
  if (typeof fileSize !== 'number' || !Number.isFinite(fileSize) || fileSize <= 0) {
    return null;
  }

  if (fileSize < 1024) {
    return `${fileSize} B`;
  }

  const kilobytes = fileSize / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} KB`;
  }

  return `${(kilobytes / 1024).toFixed(1)} MB`;
};

const getAttachmentDownloadUrl = (attachment: OutboundEmailAttachment): string | null => {
  if (!attachment.generated_document_id) {
    return null;
  }

  return `/api/rest/app/v1/documents/${attachment.generated_document_id}`;
};

export const OutboundEmailAttachmentList: React.FC<{
  attachments: OutboundEmailAttachment[];
}> = ({ attachments }) => {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.75}>
      <Typography variant="overline" color="text.secondary">
        Attachments
      </Typography>
      {attachments.map((attachment, index) => {
        const downloadUrl = getAttachmentDownloadUrl(attachment);
        const sizeLabel = formatAttachmentSize(attachment.file_size);
        const key = `${attachment.generated_document_id ?? attachment.filename}-${index}`;

        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <AttachFile fontSize="small" color="action" />
              {downloadUrl ? (
                <MUILink
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    const docWindow = preOpenWindow();
                    void openDocumentWithAuth(downloadUrl, docWindow);
                  }}
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {attachment.filename}
                </MUILink>
              ) : (
                <Typography sx={{ fontWeight: 700 }}>{attachment.filename}</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              {sizeLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {sizeLabel}
                </Typography>
              ) : null}
              {downloadUrl ? null : (
                <Typography variant="caption" color="text.secondary">
                  Download unavailable
                </Typography>
              )}
              <Chip
                size="small"
                label={attachment.content_type ?? 'attachment'}
                variant="outlined"
                sx={{ textTransform: 'uppercase', fontWeight: 700 }}
              />
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
